import type { AppEnv, UtilsManager as UtilsManagerType } from '@rpa/shared/platform'

import type { DialogObj } from '../types'

import ClipboardManager from './clipboardManager'

const { ipcRenderer } = window.electron

function getAppEnv(): AppEnv {
  return 'electron'
}

function openInBrowser(url: string) {
  ipcRenderer.send('open-in-browser', url)
}

function listenEvent(eventName: string, callback: (data: any) => void) {
  ipcRenderer.on(eventName, (_event, data) => callback(data))
}

function getFromElectronInfo<T>(key: string, defaultValue: T): Promise<T> {
  return new Promise((resolve) => {
    const electronInfo = localStorage.getItem('electron');
    if (electronInfo) {
      try {
        const info = JSON.parse(electronInfo);
        resolve(info[key] ?? defaultValue);
      } catch (e) {
        console.error('Failed to parse electron info from localStorage', e);
        resolve(defaultValue);
      }
    } else {
      resolve(defaultValue);
    }
  });
}

function getAppVersion() {
  return getFromElectronInfo<string>('appVersion', 'latest')
}

const getAppConfig: UtilsManagerType['getAppConfig'] = () => {
  return ipcRenderer.invoke('get-app-config')
}

function getAppPath() {
  return getFromElectronInfo<string>('appPath', '')
}

function getUserPath() {
  return getFromElectronInfo<string>('userDataPath', '')
}

function getResourcePath() {
  return getFromElectronInfo<string>('resourcePath', '')
}

async function getBuildInfo() {
  const electronVersion = await getFromElectronInfo<string>('electronVersion', '')
  return `Build by Electron ${electronVersion}`
}

function getSystemEnv() {
  return new Promise<string>((resolve) => {
    const electronInfo = localStorage.getItem('electron')
    if (electronInfo) {
      const { arch, platform, release } = JSON.parse(electronInfo)
      const sysInfo = `${release} ${platform} ${arch} `
      resolve(sysInfo)
    }
    else {
      resolve('')
    }
  })
}

function invoke(channel: string, ...args: any[]) {
  return ipcRenderer.invoke(channel, ...args)
}

const readFile: UtilsManagerType['readFile'] = (filePath, encoding) => {
  return ipcRenderer.invoke('read-file', filePath, encoding)
}

const saveFile: UtilsManagerType['saveFile'] = (fileName, buffer) => {
  return ipcRenderer.invoke('save-file', fileName, buffer)
}

function playVideo(videoPath: string) {
  ipcRenderer.invoke('open-path', videoPath).then((res) => {
    if (res) {
      console.log('Video played successfully')
    }
    else {
      console.error('Failed to play video')
    }
  })
}

function pathJoin(dirArr: Array<string>) {
  return ipcRenderer.invoke('path-join', ...dirArr)
}

function shellopen(path: string) {
  return new Promise<void>((resolve, reject) => {
    const fullPath = path.replace(/\\/g, '/')
    console.log('fullPath: ', fullPath)
    ClipboardManager.writeClipboardText(fullPath)
    ipcRenderer
      .invoke('open-path', fullPath)
      .then((res) => {
        if (res) {
          console.log('Shell opened successfully')
          resolve()
        }
        else {
          console.error('Failed to open shell')
          reject(new Error('Failed to open shell'))
        }
      })
      .catch((err) => {
        console.error('Error opening shell:', err)
        reject(err)
      })
  })
}

async function openPlugins() {
  const userDataPath = await getUserPath()
  return shellopen(`${userDataPath.replace(/[\\/]+$/, '')}/browser-extension`)
}

const showDialog: UtilsManagerType['showDialog'] = async (dialogProps) => {
  const { file_type, filters: dialogFilters, multiple, defaultPath = '' } = dialogProps
  const isDirectory = file_type === 'folder' // 默认打开文件
  const isMultiple = file_type === 'files' ? true : (file_type === 'file' ? multiple : false) // 默认单选
  const filterExtensions = dialogFilters?.map((item: string) => item.replace('.', ''))
  const filters = filterExtensions ? [{ name: '', extensions: filterExtensions }] : []

  const properties: string[] = [
    isDirectory ? 'openDirectory' : 'openFile',
    isMultiple ? 'multiSelections' : ''
  ]

  const dialogObj: DialogObj = { title: '选择文件目录', defaultPath, properties, filters }
  return await ipcRenderer.invoke('open-dialog', dialogObj)
}

const getPluginList: UtilsManagerType['getPluginList'] = async () => {
  return ipcRenderer.invoke('get-plugin-list')
}

const UtilsManager: UtilsManagerType = {
  getAppEnv,
  getAppPath,
  getAppVersion,
  getAppConfig,
  getBuildInfo,
  getSystemEnv,
  getUserPath,
  listenEvent,
  readFile,
  saveFile,
  invoke,
  openInBrowser,
  openPlugins,
  pathJoin,
  playVideo,
  shellopen,
  showDialog,
  getPluginList,
  getResourcePath,
}

export default UtilsManager
